import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import FormModal from "flarum/common/components/FormModal";
import Avatar from "flarum/common/components/Avatar";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import username from "flarum/common/helpers/username";
import classList from "flarum/common/utils/classList";
/** What the endpoint accepts in one call — mirrors ChannelResource::addMembers. */
const MAX_PER_REQUEST = 50;
/**
 * Picks people to put into a channel.
 *
 * A modal rather than the field that used to unfold inside the members tab.
 * That field could only add one person per click, each one its own request and
 * its own alert, and it shared the tab with the member list and the member
 * filter — three search-shaped controls stacked on top of each other, two of
 * which searched different things. Choosing several people is one task, so it
 * gets one surface and one request.
 */
export default class AddMembersModal extends FormModal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "query", "");
    _defineProperty(this, "results", []);
    _defineProperty(this, "selected", []);
    _defineProperty(this, "searching", false);
    _defineProperty(this, "timer", null);
    /**
     * Guards against an earlier search resolving after a later one.
     *
     * Typing "ram" issues a request for "ra" and one for "ram"; without this the
     * slower of the two wins and the list contradicts the field.
     */
    _defineProperty(this, "sequence", 0);
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.timer !== null) window.clearTimeout(this.timer);
  }
  className() {
    return "ChatModal ChatAddMembers Modal--medium";
  }
  title() {
    return app.translator.trans("ramon-chat.forum.add_members.title");
  }
  content() {
    return m("div", {
      className: "Modal-body ChatAddMembers-body"
    }, m("div", {
      className: "ChatAddMembers-search"
    }, m("i", {
      className: "ChatAddMembers-search-icon fas fa-magnifying-glass",
      "aria-hidden": "true"
    }), m("input", {
      className: "ChatAddMembers-search-input",
      type: "search",
      placeholder: app.translator.trans("ramon-chat.forum.add_members.search_placeholder", {}, true),
      value: this.query,
      disabled: this.loading,
      oninput: e => this.search(e.target.value),
      oncreate: vnode => vnode.dom.focus()
    }), this.searching ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : null), this.chips(), this.candidates());
  }

  /**
   * The current selection, as removable chips.
   *
   * Kept outside the results list because a chosen person stops matching as soon
   * as the query changes — without this, selecting three people and typing a
   * fourth name leaves nothing on screen saying who is about to be added.
   */
  chips() {
    if (this.selected.length === 0) return null;
    return m("div", {
      className: "ChatAddMembers-chips"
    }, this.selected.map(user => m("button", {
      type: "button",
      key: user.id(),
      className: "ChatAddMembers-chip",
      disabled: this.loading,
      title: app.translator.trans("ramon-chat.forum.add_members.deselect", {
        username: username(user)
      }, true),
      onclick: () => this.toggle(user)
    }, m(Avatar, {
      user: user,
      className: "Avatar"
    }), m("span", null, username(user)), m("i", {
      className: "fas fa-xmark",
      "aria-hidden": "true"
    }))));
  }
  candidates() {
    const typed = this.query.trim().length;
    if (typed < 2) {
      return m("p", {
        className: "ChatAddMembers-hint"
      }, app.translator.trans("ramon-chat.forum.add_members.hint"));
    }
    if (this.results.length === 0) {
      return m("p", {
        className: "ChatAddMembers-hint"
      }, this.searching ? app.translator.trans("ramon-chat.forum.add_members.searching") : app.translator.trans("ramon-chat.forum.add_members.empty"));
    }
    return m("div", {
      className: "ChatAddMembers-results"
    }, this.results.map(user => {
      const picked = this.isSelected(user);
      return m("button", {
        type: "button",
        key: user.id(),
        className: classList("ChatAddMembers-result", {
          "ChatAddMembers-result--selected": picked
        })
        // Checkbox semantics: the row is a toggle, not a command, and the
        // selection is only applied when the form is submitted.
        ,
        role: "checkbox",
        "aria-checked": picked,
        disabled: this.loading,
        onclick: () => this.toggle(user)
      }, m(Avatar, {
        user: user,
        className: "Avatar"
      }), m("span", {
        className: "ChatAddMembers-result-name"
      }, username(user)), m("span", {
        className: "ChatAddMembers-result-mark",
        "aria-hidden": "true"
      }, m("i", {
        className: picked ? "fas fa-check" : "fas fa-plus"
      })));
    }));
  }
  footer() {
    const count = this.selected.length;
    const tooMany = count > MAX_PER_REQUEST;
    return m("div", {
      className: "Modal-footer ChatAddMembers-footer"
    }, tooMany ? m("span", {
      className: "ChatAddMembers-footer-warning"
    }, app.translator.trans("ramon-chat.api.members_too_many", {
      max: MAX_PER_REQUEST
    })) : null, m(Button, {
      className: "Button Button--link",
      type: "button",
      disabled: this.loading,
      onclick: () => this.hide()
    }, app.translator.trans("ramon-chat.forum.new_channel.cancel")), m(Button, {
      className: "Button Button--primary",
      type: "submit",
      loading: this.loading,
      disabled: this.loading || count === 0 || tooMany
    }, count === 0 ? app.translator.trans("ramon-chat.forum.add_members.submit") : app.translator.trans("ramon-chat.forum.add_members.submit_count", {
      count
    })));
  }
  inner() {
    return m('[', null, super.inner(), this.footer());
  }

  // ── Behaviour ──────────────────────────────────────────────────────────────

  isSelected(user) {
    return this.selected.some(picked => picked.id() === user.id());
  }
  toggle(user) {
    this.selected = this.isSelected(user) ? this.selected.filter(picked => picked.id() !== user.id()) : [...this.selected, user];
  }

  /** Debounced so typing does not issue a request per keystroke. */
  search(value) {
    this.query = value;
    if (this.timer !== null) window.clearTimeout(this.timer);
    if (value.trim().length < 2) {
      this.results = [];
      this.searching = false;
      return;
    }
    this.searching = true;
    const mine = ++this.sequence;
    this.timer = window.setTimeout(() => {
      app.store.find("users", {
        filter: {
          q: value.trim()
        },
        page: {
          limit: 10
        }
      }).then(results => {
        if (mine !== this.sequence) return;
        const already = new Set(this.attrs.existing.map(member => member.id()));

        // Someone already in the channel is not a candidate; offering them and
        // then silently doing nothing is worse than not offering.
        this.results = (Array.isArray(results) ? results : []).filter(user => !already.has(user.id()));
        this.searching = false;
        m.redraw();
      }).catch(() => {
        if (mine !== this.sequence) return;
        this.results = [];
        this.searching = false;
        app.alerts.show({
          type: "error"
        }, app.translator.trans("ramon-chat.forum.add_members.search_failed"));
        m.redraw();
      });
    }, 250);
  }
  onsubmit(e) {
    e.preventDefault();
    if (this.loading || this.selected.length === 0) return;
    this.loading = true;
    m.redraw();
    const chosen = this.selected;
    app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(this.attrs.channel.id(), "/members"),
      // One request for the whole selection: the endpoint takes a list, and
      // adding five people used to be five requests, five notifications'
      // worth of round trips and five chances to half-fail.
      body: {
        data: {
          attributes: {
            userIds: chosen.map(u => Number(u.id()))
          }
        }
      }
    }).then(payload => {
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.hide();
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.add_members.added", {
        count: chosen.length
      }));
      this.attrs.onAdded(chosen);
    }).catch(error => {
      this.loading = false;
      if (error != null && error.alert) {
        this.onerror(error);
      } else {
        var _error$response$error, _error$response;
        app.alerts.show({
          type: "error"
        }, (_error$response$error = error == null || (_error$response = error.response) == null || (_error$response = _error$response.errors) == null || (_error$response = _error$response[0]) == null ? void 0 : _error$response.detail) != null ? _error$response$error : app.translator.trans("ramon-chat.forum.add_members.failed"));
        m.redraw();
      }
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/AddMembersModal', AddMembersModal);